import { useCallback, useEffect, useRef } from "react";
import { adminInputClass } from "../shared/adminStyles";
import { getItemAmount, getQuotationTotal } from "./quotationConfig";

const sanitizeDigits = (value) => value.replace(/[^\d]/g, "");

const sanitizeDecimal = (value) => {
  let cleaned = value.replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }
  return cleaned;
};

const QuotationForm = ({
  form,
  onQuotationNoChange,
  onBillTitleChange,
  onDateChange,
  onCompanyNameChange,
  onAddItemRow,
  onSetItemField,
  onRemoveItemRow,
  onSubmit,
}) => {
  const quotationNoRef = useRef(null);
  const billTitleRef = useRef(null);
  const dateRef = useRef(null);
  const companyNameRef = useRef(null);
  const descriptionRefs = useRef([]);
  const qtyRefs = useRef([]);
  const rateRefs = useRef([]);
  const unitRefs = useRef([]);

  useEffect(() => {
    billTitleRef.current?.focus();
  }, []);

  // Every focusable field in the form, in visual order. Rebuilt on every
  // navigation key press so it always reflects the current row count —
  // this is what lets Up/Down/Enter move through the *whole* form
  // (Quotation No -> Bill Title -> Company Name -> every row's 4 fields)
  // as one continuous chain, instead of separate disconnected sections.
  const buildSequence = useCallback(() => {
    const seq = [];
    if (quotationNoRef.current) seq.push(quotationNoRef.current);
    if (billTitleRef.current) seq.push(billTitleRef.current);
    if (dateRef.current) seq.push(dateRef.current);
    if (companyNameRef.current) seq.push(companyNameRef.current);
    form.items.forEach((_, index) => {
      [descriptionRefs, qtyRefs, rateRefs, unitRefs].forEach((refs) => {
        if (refs.current[index]) seq.push(refs.current[index]);
      });
    });
    return seq;
  }, [form.items]);

  const addItemRowAndFocus = useCallback(
    (afterIndex) => {
      onAddItemRow(afterIndex);
      window.setTimeout(() => {
        descriptionRefs.current[afterIndex + 1]?.focus();
      }, 0);
    },
    [onAddItemRow],
  );

  const removeItemRowAndFocus = useCallback(
    (index, field) => {
      onRemoveItemRow(index);
      window.setTimeout(() => {
        const nextIndex = Math.max(0, index - 1);
        const refsByField = {
          description: descriptionRefs,
          qty: qtyRefs,
          rate: rateRefs,
          unit: unitRefs,
        };
        refsByField[field]?.current[nextIndex]?.focus();
      }, 0);
    },
    [onRemoveItemRow],
  );

  // Single handler, attached to every field in the form (top fields and
  // every product row alike). It reads data-row / data-field off the
  // element itself, so no per-field wiring is needed — this is what
  // guarantees you can always move back to any earlier field, including
  // Qty or Product Name of the same row.
  const handleFieldKeyDown = (event) => {
    const { field, row } = event.target.dataset;
    const isRowField = row !== undefined;
    const rowIndex = isRowField ? Number(row) : null;
    const lastRowIndex = form.items.length - 1;
    const isLastFieldOfLastRow =
      isRowField && field === "unit" && rowIndex === lastRowIndex;

    const seq = buildSequence();
    const currentIndex = seq.indexOf(event.target);

    const goTo = (index) => {
      if (index < 0 || index >= seq.length) return;
      seq[index].focus();
    };

    if (event.key === "Enter") {
      event.preventDefault();
      if (isLastFieldOfLastRow) {
        addItemRowAndFocus(rowIndex);
      } else {
        goTo(currentIndex + 1);
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (isLastFieldOfLastRow) {
        addItemRowAndFocus(rowIndex);
      } else {
        goTo(currentIndex + 1);
      }
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      goTo(currentIndex - 1);
      return;
    }

    if (event.key === "ArrowRight") {
      const atEnd = event.target.selectionStart === event.target.value.length;
      if (!atEnd) return;
      event.preventDefault();
      if (isLastFieldOfLastRow) {
        addItemRowAndFocus(rowIndex);
      } else {
        goTo(currentIndex + 1);
      }
      return;
    }

    if (event.key === "ArrowLeft") {
      const atStart = event.target.selectionStart === 0;
      if (!atStart) return;
      event.preventDefault();
      goTo(currentIndex - 1);
      return;
    }

    if (
      (event.key === "Backspace" || event.key === "Delete") &&
      event.ctrlKey
    ) {
      if (isRowField) {
        event.preventDefault();
        removeItemRowAndFocus(rowIndex, field);
      }
    }
  };

  const total = getQuotationTotal(form.items);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            Quotation No
          </label>
          <input
            ref={quotationNoRef}
            type="text"
            inputMode="numeric"
            data-field="quotationNo"
            value={form.quotationNo}
            onChange={(event) =>
              onQuotationNoChange(sanitizeDigits(event.target.value))
            }
            onKeyDown={handleFieldKeyDown}
            className={adminInputClass}
            placeholder="e.g. 240"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Bill Title</label>
          <input
            ref={billTitleRef}
            type="text"
            data-field="billTitle"
            value={form.billTitle}
            onChange={(event) => onBillTitleChange(event.target.value)}
            onKeyDown={handleFieldKeyDown}
            className={adminInputClass}
            placeholder="e.g. Quotation, Estimate..."
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Date</label>
          <input
            ref={dateRef}
            type="date"
            data-field="date"
            value={form.date}
            onChange={(event) => onDateChange(event.target.value)}
            onKeyDown={handleFieldKeyDown}
            className={adminInputClass}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            Company Name
          </label>
          <input
            ref={companyNameRef}
            type="text"
            data-field="companyName"
            value={form.companyName}
            onChange={(event) => onCompanyNameChange(event.target.value)}
            onKeyDown={handleFieldKeyDown}
            className={adminInputClass}
            placeholder="e.g. Unique Traders-HRC-Jay Shree Complex"
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">Products</p>
          <button
            type="button"
            onClick={() => onAddItemRow(form.items.length - 1)}
            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100"
          >
            Add Product
          </button>
        </div>

        {form.items.map((item, index) => {
          const amount = getItemAmount(item);
          return (
            <div key={index} className="rounded-xl border border-gray-200 p-3">
              <div className="grid gap-3 md:grid-cols-[1fr_90px_100px_90px_110px_80px]">
                <div>
                  <label className="mb-1 block text-xs text-gray-500">
                    Product Name
                  </label>
                  <input
                    ref={(el) => {
                      descriptionRefs.current[index] = el;
                    }}
                    type="text"
                    data-row={index}
                    data-field="description"
                    value={item.description}
                    onChange={(event) =>
                      onSetItemField(index, "description", event.target.value)
                    }
                    onKeyDown={handleFieldKeyDown}
                    className={adminInputClass}
                    placeholder="e.g. Prama IP Camera Model-PT NC120D3-N"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">
                    Qty
                  </label>
                  <input
                    ref={(el) => {
                      qtyRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    data-row={index}
                    data-field="qty"
                    value={item.qty}
                    onChange={(event) =>
                      onSetItemField(
                        index,
                        "qty",
                        sanitizeDigits(event.target.value),
                      )
                    }
                    onKeyDown={handleFieldKeyDown}
                    className={adminInputClass}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">
                    Rate
                  </label>
                  <input
                    ref={(el) => {
                      rateRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="decimal"
                    data-row={index}
                    data-field="rate"
                    value={item.rate}
                    onChange={(event) =>
                      onSetItemField(
                        index,
                        "rate",
                        sanitizeDecimal(event.target.value),
                      )
                    }
                    onKeyDown={handleFieldKeyDown}
                    className={adminInputClass}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">
                    Unit
                  </label>
                  <input
                    ref={(el) => {
                      unitRefs.current[index] = el;
                    }}
                    type="text"
                    data-row={index}
                    data-field="unit"
                    value={item.unit}
                    onChange={(event) =>
                      onSetItemField(index, "unit", event.target.value)
                    }
                    onKeyDown={handleFieldKeyDown}
                    className={adminInputClass}
                    placeholder="Pcs"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">
                    Amount
                  </label>
                  <div className="flex h-[38px] items-center rounded-lg border border-gray-100 bg-gray-50 px-3 text-sm font-medium text-gray-700">
                    {amount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => onRemoveItemRow(index)}
                    className="w-full rounded-lg border border-red-200 bg-red-50 px-2 py-2 text-xs font-medium text-red-500 hover:bg-red-100"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        <p className="text-[10px] text-gray-400">
          Enter, →, or ↓ moves forward through every field including from
          Quotation No through Bill Title, Company Name, and each product row.
          ←/↑ moves back the same way, so you can always return to an earlier
          field like Qty or Product Name. Use Ctrl+Backspace or Ctrl+Delete to
          remove the current row.
        </p>

        <div className="flex justify-end rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="text-sm font-semibold text-gray-700">
            Total:{" "}
            <span className="text-gray-900">
              {total.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationForm;
